import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSignalR } from './use-signalr';
import { useCurrentOrgContext } from './use-current-org';
import type { OrgStatus } from '../types/organization';

export interface OrgStatusInfo {
  status: OrgStatus | null;
  isSuspended: boolean;
  isPendingApproval: boolean;
}

export interface OrganizationSuspendedPayload {
  orgId: string;
  orgName: string;
  reason: string;
}

export interface OrganizationReactivatedPayload {
  orgId: string;
  orgName: string;
}

/**
 * Reads the current organization's lifecycle status from context. Suspended and
 * pending-approval are deliberately distinct so the UI can treat them differently.
 */
export function useOrgStatus(): OrgStatusInfo {
  const { orgStatus } = useCurrentOrgContext();
  return {
    status: orgStatus,
    isSuspended: orgStatus === 'Suspended',
    isPendingApproval: orgStatus === 'PendingApproval',
  };
}

/**
 * Subscribes to live organization-status pushes on the org-scoped devices hub and
 * keeps the org context status in sync. Mount ONCE inside the app shell so a
 * suspension/reactivation is reflected on whatever page the user is viewing.
 *
 * The devices hub connection is pooled by useSignalR (keyed by hub + orgId), so this
 * shares the same connection as device/detection updates rather than opening a new one.
 */
export function useOrgStatusSync() {
  const { setOrgStatus, currentOrg } = useCurrentOrgContext();
  const queryClient = useQueryClient();
  const { connection, status } = useSignalR({ hubPath: '/bff/hubs/devices' });
  // Reason is only carried on the live push (not the load-time seed), so it's held
  // here rather than in context. Null when suspended state came from a page reload.
  const [suspensionReason, setSuspensionReason] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!connection) return;

    const handleSuspended = (payload: OrganizationSuspendedPayload) => {
      // The connection is org-scoped, but guard defensively against a stale group.
      if (currentOrg && payload.orgId !== currentOrg.orgId) return;
      setSuspensionReason(payload.reason || null);
      setOrgStatus('Suspended');
    };

    const handleReactivated = (payload: OrganizationReactivatedPayload) => {
      if (currentOrg && payload.orgId !== currentOrg.orgId) return;
      setSuspensionReason(null);
      setOrgStatus('Active');
      // Data may have been withheld while suspended — refetch now that access is back.
      queryClient.invalidateQueries();
    };

    connection.on('OrganizationSuspended', handleSuspended);
    connection.on('OrganizationReactivated', handleReactivated);
    return () => {
      connection.off('OrganizationSuspended', handleSuspended);
      connection.off('OrganizationReactivated', handleReactivated);
    };
  }, [connection, setOrgStatus, queryClient, currentOrg]);

  return { connectionStatus: status, suspensionReason };
}
